import { LEFT_FIELD_SCENARIOS } from './leftFieldScenarios'
import type {
  GameState,
  LeftFieldArchetype,
  LeftFieldChoice,
  LeftFieldDelayedEffect,
  LeftFieldEvent,
  LeftFieldOutcome,
  LeftFieldScenario,
  LeftFieldSeverity,
  LeftFieldState,
  Resources,
} from './types'

const ARCHETYPES: readonly LeftFieldArchetype[] = [
  'player-matter',
  'staff-matter',
  'football-operations',
  'board-ownership',
  'supporters-media',
  'opportunity',
  'ethical-decision',
  'tactical-shock',
]
const SEVERITIES: readonly LeftFieldSeverity[] = ['minor', 'moderate', 'major', 'critical']
const SEVERITY_WEIGHT: Record<LeftFieldSeverity, number> = { minor: 1, moderate: 0.68, major: 0.34, critical: 0.14 }

export const MAX_LEFT_FIELD_DELAY_ROUNDS = 50

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T
const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value))
const finite = (value: unknown, fallback = 0) => Number.isFinite(value) ? Number(value) : fallback

// FNV-1a followed by a final avalanche. Keys, not call order, define every draw.
const hash = (value: string): number => {
  let result = 2166136261
  for (let index = 0; index < value.length; index += 1) result = Math.imul(result ^ value.charCodeAt(index), 16777619)
  result ^= result >>> 16
  result = Math.imul(result, 0x7feb352d)
  result ^= result >>> 15
  result = Math.imul(result, 0x846ca68b)
  result ^= result >>> 16
  return result >>> 0
}
const draw = (key: string) => hash(key) / 0x100000000
const identity = (state: GameState) => {
  const season = state.season?.season ?? 1
  const stableSeed = state.season?.careerSeed ?? String(state.season?.seed ?? state.seed)
  return `${stableSeed}:${state.season?.seed ?? state.seed}:s${season}:r${state.round}`
}
const roundKey = (state: GameState) => `s${state.season?.season ?? 1}-r${state.round}`
const WEEK_PLANNING_PHASES = new Set([
  'headquarters', 'left-field', 'player-detail', 'team-review', 'tactics-review', 'confirmation',
  'squad', 'development', 'team', 'tactics', 'research', 'board', 'staff', 'scouting', 'recruitment', 'league', 'club', 'history',
])

export const createInitialLeftFieldState = (): LeftFieldState => ({
  version: 53,
  currentEvent: null,
  eventHistory: [],
  pendingDelayedEffects: [],
  processedRounds: [],
})

const scenarioById = (id: string) => LEFT_FIELD_SCENARIOS.find(scenario => scenario.id === id)
const RESOURCE_KEYS = ['cash', 'research', 'development', 'institutional'] as const
const REQUIREMENT_KEYS = ['minCash', 'minResearch', 'minDevelopment', 'minInstitutional', 'hasFacility', 'hasStaff'] as const
const FACILITY_TYPES = ['training-ground', 'medical-centre', 'stadium', 'academy'] as const
const STAFF_SPECIALTIES = ['attack', 'defence', 'development', 'conditioning'] as const
const EVENT_STATUSES = ['pending', 'resolved', 'ignored'] as const
const RESOLUTION_PATHS = ['coach', 'delegated'] as const

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)
const isNonEmptyString = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0
const isFiniteNumber = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value)
const isPositiveInteger = (value: unknown): value is number => isFiniteNumber(value) && Number.isInteger(value) && value >= 1
const hasOnlyKeys = (value: Record<string, unknown>, keys: readonly string[]) => Object.keys(value).every(key => keys.includes(key))
const oneOf = <T extends string>(value: unknown, values: readonly T[]): value is T => typeof value === 'string' && values.includes(value as T)
const sanitiseDelayRounds = (value: unknown): number | null => isFiniteNumber(value)
  ? clamp(Math.floor(value), 1, MAX_LEFT_FIELD_DELAY_ROUNDS)
  : null

const sanitiseResourceDelta = (value: unknown): Partial<Resources> | null => {
  if (!isRecord(value) || !hasOnlyKeys(value, RESOURCE_KEYS)) return null
  const result: Partial<Resources> = {}
  for (const key of RESOURCE_KEYS) {
    if (value[key] !== undefined) {
      if (!isFiniteNumber(value[key])) return null
      result[key] = value[key]
    }
  }
  return result
}

const sanitiseRequirement = (value: unknown): LeftFieldChoice['requires'] | null => {
  if (!isRecord(value) || !hasOnlyKeys(value, REQUIREMENT_KEYS)) return null
  const result: NonNullable<LeftFieldChoice['requires']> = {}
  for (const key of ['minCash', 'minResearch', 'minDevelopment', 'minInstitutional'] as const) {
    if (value[key] !== undefined) {
      if (!isFiniteNumber(value[key]) || value[key] < 0) return null
      result[key] = value[key]
    }
  }
  if (value.hasFacility !== undefined) {
    if (!isRecord(value.hasFacility)
      || !hasOnlyKeys(value.hasFacility, ['type', 'minLevel'])
      || !oneOf(value.hasFacility.type, FACILITY_TYPES)
      || !isFiniteNumber(value.hasFacility.minLevel)
      || !Number.isInteger(value.hasFacility.minLevel)
      || value.hasFacility.minLevel < 0
      || value.hasFacility.minLevel > 3) return null
    result.hasFacility = { type: value.hasFacility.type, minLevel: value.hasFacility.minLevel }
  }
  if (value.hasStaff !== undefined) {
    if (!isRecord(value.hasStaff)
      || !hasOnlyKeys(value.hasStaff, ['specialty'])
      || !oneOf(value.hasStaff.specialty, STAFF_SPECIALTIES)) return null
    result.hasStaff = { specialty: value.hasStaff.specialty }
  }
  return result
}

const sanitiseDelayedEffect = (value: unknown): LeftFieldDelayedEffect | null => {
  if (!isRecord(value) || !isNonEmptyString(value.description)) return null
  const rounds = sanitiseDelayRounds(value.rounds)
  if (rounds === null) return null
  const resourceDelta = sanitiseResourceDelta(value.resourceDelta)
  if (!resourceDelta) return null
  const inferredKind = value.followUpArchetype ? 'follow-up' : undefined
  const kind = value.kind ?? inferredKind
  if (kind !== undefined && !oneOf(kind, ['resource', 'follow-up'])) return null
  if (value.id !== undefined && !isNonEmptyString(value.id)) return null
  if (value.sourceEventId !== undefined && !isNonEmptyString(value.sourceEventId)) return null
  if (value.dueSeason !== undefined && !isPositiveInteger(value.dueSeason)) return null
  if (value.dueRound !== undefined && !isPositiveInteger(value.dueRound)) return null
  if (value.followUpArchetype !== undefined && !oneOf(value.followUpArchetype, ARCHETYPES)) return null
  if (kind === 'follow-up' && !oneOf(value.followUpArchetype, ARCHETYPES)) return null
  return {
    rounds,
    resourceDelta,
    description: value.description,
    ...(value.id === undefined ? {} : { id: value.id }),
    ...(value.dueSeason === undefined ? {} : { dueSeason: value.dueSeason }),
    ...(value.dueRound === undefined ? {} : { dueRound: value.dueRound }),
    ...(value.sourceEventId === undefined ? {} : { sourceEventId: value.sourceEventId }),
    ...(kind === undefined ? {} : { kind }),
    ...(value.followUpArchetype === undefined ? {} : { followUpArchetype: value.followUpArchetype }),
  }
}

const sanitiseOutcome = (value: unknown): LeftFieldOutcome | null => {
  if (!isRecord(value)
    || (value.id !== undefined && !isNonEmptyString(value.id))
    || !isFiniteNumber(value.probability)
    || value.probability < 0
    || !isNonEmptyString(value.description)) return null
  const resourceDelta = sanitiseResourceDelta(value.resourceDelta)
  if (!resourceDelta) return null
  if (value.delayedEffects !== undefined && !Array.isArray(value.delayedEffects)) return null
  const delayedEffects = Array.isArray(value.delayedEffects)
    ? value.delayedEffects.map(sanitiseDelayedEffect).filter((effect): effect is LeftFieldDelayedEffect => effect !== null)
    : undefined
  let followUp: LeftFieldOutcome['followUp']
  if (value.followUp !== undefined) {
    const minRounds = isRecord(value.followUp) ? sanitiseDelayRounds(value.followUp.minRounds) : null
    const maxRounds = isRecord(value.followUp) ? sanitiseDelayRounds(value.followUp.maxRounds) : null
    if (!isRecord(value.followUp)
      || !oneOf(value.followUp.archetype, ARCHETYPES)
      || minRounds === null
      || maxRounds === null
      || !isFiniteNumber(value.followUp.probability)
      || value.followUp.probability < 0
      || value.followUp.probability > 1) return null
    followUp = {
      archetype: value.followUp.archetype,
      minRounds,
      maxRounds: Math.max(minRounds, maxRounds),
      probability: value.followUp.probability,
    }
  }
  return {
    ...(value.id === undefined ? {} : { id: value.id }),
    probability: value.probability,
    description: value.description,
    resourceDelta,
    ...(delayedEffects === undefined ? {} : { delayedEffects }),
    ...(followUp === undefined ? {} : { followUp }),
  }
}

const sanitiseChoice = (value: unknown): LeftFieldChoice | null => {
  if (!isRecord(value)
    || !isNonEmptyString(value.id)
    || !isNonEmptyString(value.label)
    || !isNonEmptyString(value.description)
    || !Array.isArray(value.outcomes)) return null
  const resourceDelta = sanitiseResourceDelta(value.resourceDelta)
  if (!resourceDelta) return null
  const outcomes = value.outcomes.map(sanitiseOutcome).filter((outcome): outcome is LeftFieldOutcome => outcome !== null)
  if (outcomes.length < 2 || outcomes.every(outcome => outcome.probability <= 0)) return null
  const requires = value.requires === undefined ? undefined : sanitiseRequirement(value.requires)
  if (value.requires !== undefined && !requires) return null
  return { id: value.id, label: value.label, description: value.description, resourceDelta, outcomes, ...(requires ? { requires } : {}) }
}

const sanitiseChoices = (value: unknown): LeftFieldChoice[] | null => {
  if (!Array.isArray(value)) return null
  const choices = value.map(sanitiseChoice).filter((choice): choice is LeftFieldChoice => choice !== null)
  return choices.length ? choices : null
}

const sanitiseEvent = (value: unknown, current: boolean): LeftFieldEvent | null => {
  if (!isRecord(value)
    || !isNonEmptyString(value.id)
    || !isNonEmptyString(value.scenarioId)
    || !isPositiveInteger(value.season)
    || !isPositiveInteger(value.round)
    || !oneOf(value.status, EVENT_STATUSES)
    || (current && value.status !== 'pending')) return null
  const scenario = scenarioById(value.scenarioId)
  const ownChoices = sanitiseChoices(value.choices)
  const catalogueChoices = scenario ? sanitiseChoices(scenario.choices) : null
  if (value.choices !== undefined && !ownChoices && !current) return null
  const choices = ownChoices ?? catalogueChoices
  if (!choices) return null
  const archetype = oneOf(value.archetype, ARCHETYPES) ? value.archetype : scenario?.archetype
  const severity = oneOf(value.severity, SEVERITIES) ? value.severity : scenario?.severity ?? 'moderate'
  if (!archetype) return null
  if (value.title !== undefined && !isNonEmptyString(value.title)) return null
  if (value.narrative !== undefined && typeof value.narrative !== 'string') return null
  if (value.chosenChoiceId !== undefined && !isNonEmptyString(value.chosenChoiceId)) return null
  if (value.resolvedOutcomeId !== undefined && !isNonEmptyString(value.resolvedOutcomeId)) return null
  if (value.resolutionPath !== undefined && !oneOf(value.resolutionPath, RESOLUTION_PATHS)) return null
  if (value.resolvedOutcomes !== undefined && !Array.isArray(value.resolvedOutcomes)) return null
  const resolvedOutcomes = Array.isArray(value.resolvedOutcomes)
    ? value.resolvedOutcomes.map(sanitiseOutcome).filter((outcome): outcome is LeftFieldOutcome => outcome !== null)
    : undefined
  if (value.pendingDelayedEffects !== undefined && !Array.isArray(value.pendingDelayedEffects)) return null
  const pendingDelayedEffects = Array.isArray(value.pendingDelayedEffects)
    ? value.pendingDelayedEffects.map(sanitiseDelayedEffect).filter((effect): effect is LeftFieldDelayedEffect => effect !== null)
    : undefined
  let followUpScheduled: LeftFieldEvent['followUpScheduled']
  if (value.followUpScheduled !== undefined) {
    if (!isRecord(value.followUpScheduled)
      || !oneOf(value.followUpScheduled.archetype, ARCHETYPES)
      || !isPositiveInteger(value.followUpScheduled.dueRound)
      || (value.followUpScheduled.dueSeason !== undefined && !isPositiveInteger(value.followUpScheduled.dueSeason))) return null
    followUpScheduled = {
      archetype: value.followUpScheduled.archetype,
      dueRound: value.followUpScheduled.dueRound,
      ...(value.followUpScheduled.dueSeason === undefined ? {} : { dueSeason: value.followUpScheduled.dueSeason }),
    }
  }
  return {
    id: value.id,
    scenarioId: value.scenarioId,
    season: value.season,
    round: value.round,
    status: value.status,
    archetype,
    severity,
    title: isNonEmptyString(value.title) ? value.title : scenario?.title ?? value.scenarioId,
    narrative: typeof value.narrative === 'string' ? value.narrative : scenario?.narrative ?? '',
    choices,
    ...(value.chosenChoiceId === undefined ? {} : { chosenChoiceId: value.chosenChoiceId }),
    ...(value.resolvedOutcomeId === undefined ? {} : { resolvedOutcomeId: value.resolvedOutcomeId }),
    ...(resolvedOutcomes === undefined ? {} : { resolvedOutcomes }),
    ...(value.resolutionPath === undefined ? {} : { resolutionPath: value.resolutionPath }),
    ...(pendingDelayedEffects === undefined ? {} : { pendingDelayedEffects }),
    ...(followUpScheduled === undefined ? {} : { followUpScheduled }),
  }
}

export const hydrateLeftFieldState = (saved: unknown): LeftFieldState => {
  if (!isRecord(saved)) return createInitialLeftFieldState()
  const historyById = new Map<string, LeftFieldEvent>()
  if (Array.isArray(saved.eventHistory)) for (const raw of saved.eventHistory) {
    const event = sanitiseEvent(raw, false)
    if (event) historyById.set(event.id, event)
  }
  const current = sanitiseEvent(saved.currentEvent, true)
  const pendingDelayedEffects = Array.isArray(saved.pendingDelayedEffects)
    ? saved.pendingDelayedEffects.map(sanitiseDelayedEffect).filter((effect): effect is LeftFieldDelayedEffect => effect !== null).map((effect, index) => ({
      ...effect,
      id: effect.id ?? `migrated-effect-${index}-${hash(`${effect.description}:${effect.rounds}`)}`,
    }))
    : []
  return {
    version: 53,
    currentEvent: current,
    eventHistory: [...historyById.values()],
    pendingDelayedEffects,
    processedRounds: [...new Set((Array.isArray(saved.processedRounds) ? saved.processedRounds : []).filter(isNonEmptyString))],
  }
}

const choiceEligible = (state: GameState, choice: LeftFieldChoice): boolean => {
  const requirement = choice.requires
  if (!requirement) return true
  const resources = state.club.resources
  if (requirement.minCash !== undefined && finite(resources.cash) < requirement.minCash) return false
  if (requirement.minResearch !== undefined && finite(resources.research) < requirement.minResearch) return false
  if (requirement.minDevelopment !== undefined && finite(resources.development) < requirement.minDevelopment) return false
  if (requirement.minInstitutional !== undefined && finite(resources.institutional) < requirement.minInstitutional) return false
  if (requirement.hasFacility) {
    const levels = state.facilities?.levels as unknown as Record<string, number> | undefined
    if (finite(levels?.[requirement.hasFacility.type]) < requirement.hasFacility.minLevel) return false
  }
  if (requirement.hasStaff && !state.staffMarket?.roster.some(coach => coach.specialty === requirement.hasStaff!.specialty)) return false
  return true
}

const choiceAffordable = (state: GameState, choice: LeftFieldChoice) => {
  const resources = state.club.resources
  return RESOURCE_KEYS.every(resource => {
    const cost = finite(choice.resourceDelta[resource])
    return cost >= 0 || finite(resources[resource]) >= -cost
  })
}

export const LEFT_FIELD_CONTINGENCY_CHOICE_ID = '__left-field-contingency__'

const contingencyChoice = (): LeftFieldChoice => ({
  id: LEFT_FIELD_CONTINGENCY_CHOICE_ID,
  label: 'Use the standard club contingency',
  description: 'Decline unplanned spending and let football operations contain the matter using existing resources.',
  resourceDelta: {},
  outcomes: [{
    id: 'contained',
    probability: 0.7,
    description: 'The standard process contains the matter without disrupting the week.',
    resourceDelta: {},
  }, {
    id: 'opportunity-passes',
    probability: 0.3,
    description: 'The immediate opportunity passes, but the club avoids an unaffordable commitment.',
    resourceDelta: {},
  }],
})

export const availableLeftFieldChoices = (state: GameState, event: LeftFieldEvent): LeftFieldChoice[] =>
  (event.choices ?? scenarioById(event.scenarioId)?.choices ?? []).filter(choice => choiceEligible(state, choice) && choiceAffordable(state, choice))

export interface LeftFieldChoiceAvailability {
  available: boolean
  reasons: string[]
}

export const leftFieldChoiceAvailability = (state: GameState, choice: LeftFieldChoice): LeftFieldChoiceAvailability => {
  const reasons: string[] = []
  const requirement = choice.requires
  const resources = state.club.resources
  const need = (label: string, current: number, minimum?: number) => {
    if (minimum !== undefined && current < minimum) reasons.push(`Requires ${minimum} ${label} (available: ${current})`)
  }
  need('cash', finite(resources.cash), requirement?.minCash)
  need('research', finite(resources.research), requirement?.minResearch)
  need('development', finite(resources.development), requirement?.minDevelopment)
  need('institutional capital', finite(resources.institutional), requirement?.minInstitutional)
  if (requirement?.hasFacility) {
    const current = finite((state.facilities?.levels as unknown as Record<string, number> | undefined)?.[requirement.hasFacility.type])
    if (current < requirement.hasFacility.minLevel) reasons.push(`Requires ${requirement.hasFacility.type.replaceAll('-', ' ')} level ${requirement.hasFacility.minLevel} (current: ${current})`)
  }
  if (requirement?.hasStaff && !state.staffMarket?.roster.some(coach => coach.specialty === requirement.hasStaff!.specialty)) {
    reasons.push(`Requires a ${requirement.hasStaff.specialty} staff specialist`)
  }
  const costs = choice.resourceDelta
  const unaffordable = (cost: unknown, available: unknown) => finite(cost) < 0 && finite(available) < -finite(cost)
  if (unaffordable(costs.cash, resources.cash)) reasons.push(`Cannot afford cash cost of ${Math.abs(finite(costs.cash))}`)
  if (unaffordable(costs.research, resources.research)) reasons.push(`Cannot afford research cost of ${Math.abs(finite(costs.research))}`)
  if (unaffordable(costs.development, resources.development)) reasons.push(`Cannot afford development cost of ${Math.abs(finite(costs.development))}`)
  if (unaffordable(costs.institutional, resources.institutional)) reasons.push(`Cannot afford institutional capital cost of ${Math.abs(finite(costs.institutional))}`)
  return { available: reasons.length === 0, reasons }
}

const DOMINANCE_EPSILON = 1e-9

export interface LeftFieldDominanceFinding {
  scenarioId: string
  dominatedChoiceId: string
  dominatingChoiceId: string
}

/** Conservative Pareto check: each resource stays a separate dimension. */
export const findDominatedLeftFieldChoices = (scenarios: readonly LeftFieldScenario[] = LEFT_FIELD_SCENARIOS): LeftFieldDominanceFinding[] => {
  const metrics = (choice: LeftFieldChoice) => ({
    expected: RESOURCE_KEYS.map(key => finite(choice.resourceDelta[key])
      + choice.outcomes.reduce((sum, result) => sum + result.probability * finite(result.resourceDelta[key]), 0)),
    worst: RESOURCE_KEYS.map(key => finite(choice.resourceDelta[key])
      + Math.min(0, ...choice.outcomes.map(result => finite(result.resourceDelta[key])))),
    immediateCost: RESOURCE_KEYS.map(key => Math.max(0, -finite(choice.resourceDelta[key]))),
  })
  const findings: LeftFieldDominanceFinding[] = []
  for (const scenario of scenarios) {
    for (const dominated of scenario.choices) for (const dominating of scenario.choices) {
      if (dominated === dominating) continue
      const a = metrics(dominated)
      const b = metrics(dominating)
      const noWorse = b.expected.every((value, index) => value + DOMINANCE_EPSILON >= a.expected[index])
        && b.worst.every((value, index) => value + DOMINANCE_EPSILON >= a.worst[index])
        && b.immediateCost.every((value, index) => value <= a.immediateCost[index] + DOMINANCE_EPSILON)
      const strictlyBetter = b.expected.some((value, index) => value > a.expected[index] + DOMINANCE_EPSILON)
        || b.worst.some((value, index) => value > a.worst[index] + DOMINANCE_EPSILON)
        || b.immediateCost.some((value, index) => value + DOMINANCE_EPSILON < a.immediateCost[index])
      if (noWorse && strictlyBetter) findings.push({
        scenarioId: scenario.id,
        dominatedChoiceId: dominated.id,
        dominatingChoiceId: dominating.id,
      })
    }
  }
  return findings
}

export const isLeftFieldScenarioEligible = (state: GameState, scenario: LeftFieldScenario) => {
  const season = state.season?.season ?? 1
  const when = scenario.when
  const userTeamId = state.season?.userTeamId
  const userResults = state.season && userTeamId
    ? Object.values(state.season.results).filter(result => result.homeTeamId === userTeamId || result.awayTeamId === userTeamId)
    : []
  const previous = userResults.filter(result => result.round < state.round).sort((a, b) => b.round - a.round)[0]
  const previousWasHome = previous && previous.homeTeamId === userTeamId
  const previousScore = previous && (previousWasHome ? previous.homeScore.total : previous.awayScore.total)
  const previousOpponentScore = previous && (previousWasHome ? previous.awayScore.total : previous.homeScore.total)
  const previousResult = previousScore === undefined || previousOpponentScore === undefined ? undefined
    : previousScore > previousOpponentScore ? 'win' : previousScore < previousOpponentScore ? 'loss' : 'draw'
  const contracts = state.club.contracts ?? state.contracts ?? {}
  const expiringContracts = Object.values(contracts).filter(contract => contract.yearsRemaining <= 1 && contract.status !== 'delisted').length
  const staffVacancy = (state.staffMarket?.roster.length ?? 0) < 4
  if (when?.activeFacilityBuild !== undefined && Boolean(state.facilities?.activeBuild) !== when.activeFacilityBuild) return false
  if (when?.minimumFacilityLevel && finite(state.facilities?.levels[when.minimumFacilityLevel.type]) < when.minimumFacilityLevel.level) return false
  if (when?.staffSpecialty && !state.staffMarket?.roster.some(coach => coach.specialty === when.staffSpecialty)) return false
  if (when?.staffVacancy !== undefined && staffVacancy !== when.staffVacancy) return false
  if (when?.minimumContractsExpiring !== undefined && expiringContracts < when.minimumContractsExpiring) return false
  if (when?.minimumCompletedRounds !== undefined && finite(state.season?.completedThroughRound) < when.minimumCompletedRounds) return false
  if (when?.minimumSeasonsPlayed !== undefined && season - 1 < when.minimumSeasonsPlayed) return false
  if (when?.venue && state.week?.venue !== when.venue) return false
  if (when?.previousVenue && (!previous || (previousWasHome ? 'home' : 'away') !== when.previousVenue)) return false
  if (when?.previousResult && previousResult !== when.previousResult) return false
  if (when?.maximumCash !== undefined && state.club.resources.cash > when.maximumCash) return false
  return state.round >= (scenario.minRound ?? 1)
    && state.round <= (scenario.maxRound ?? Number.MAX_SAFE_INTEGER)
    && season >= (scenario.minSeason ?? 1)
    && season <= (scenario.maxSeason ?? Number.MAX_SAFE_INTEGER)
    && scenario.choices.some(choice => choiceEligible(state, choice) && choiceAffordable(state, choice))
}

const weightedPick = <T>(items: T[], weightFor: (item: T) => number, key: string): T | undefined => {
  const weighted = items.map(item => ({ item, weight: Math.max(0, finite(weightFor(item))) })).filter(item => item.weight > 0)
  const total = weighted.reduce((sum, item) => sum + item.weight, 0)
  if (total <= 0) return undefined
  let cursor = draw(key) * total
  for (const entry of weighted) {
    cursor -= entry.weight
    if (cursor < 0) return entry.item
  }
  return weighted.at(-1)?.item
}

const eventChance = (state: GameState) => {
  // The opening week remains clean for legacy flows; scheduled weeks converge on 40%.
  if (state.round === 1) return 0
  const institutional = state.club.resources.institutional
  const cash = state.club.resources.cash
  return clamp(0.4 + (institutional < 30 ? 0.04 : institutional > 75 ? -0.03 : 0) + (cash < 3 ? 0.02 : 0), 0.34, 0.46)
}

const dueNow = (state: GameState, effect: LeftFieldDelayedEffect) => {
  const season = state.season?.season ?? 1
  return finite(effect.dueSeason, season) < season
    || (finite(effect.dueSeason, season) === season && finite(effect.dueRound, Number.MAX_SAFE_INTEGER) <= state.round)
}

const severityFor = (state: GameState, scenario: LeftFieldScenario): LeftFieldSeverity => {
  if (scenario.severity && SEVERITIES.includes(scenario.severity)) return scenario.severity
  const value = draw(`${identity(state)}:${scenario.id}:severity`)
  return value < 0.55 ? 'minor' : value < 0.85 ? 'moderate' : value < 0.97 ? 'major' : 'critical'
}

const makeEvent = (state: GameState, scenario: LeftFieldScenario): LeftFieldEvent => ({
  id: `left-field-${roundKey(state)}-${scenario.id}`,
  scenarioId: scenario.id,
  season: state.season?.season ?? 1,
  round: state.round,
  status: 'pending',
  archetype: scenario.archetype,
  severity: severityFor(state, scenario),
  title: scenario.title,
  narrative: scenario.narrative,
  choices: clone(sanitiseChoices(scenario.choices) ?? []),
})

export const applyDueLeftFieldEffects = (state: GameState): GameState => {
  const leftField = hydrateLeftFieldState(state.leftField)
  const due = leftField.pendingDelayedEffects.filter(effect => effect.kind !== 'follow-up' && dueNow(state, effect))
  if (!due.length) return state.leftField === leftField ? state : { ...state, leftField }
  const resources = due.filter(effect => effect.kind !== 'follow-up').reduce(
    (current, effect) => applyResourceDelta(state, current, effect.resourceDelta),
    state.club.resources,
  )
  const dueIds = new Set(due.map(effect => effect.id))
  return {
    ...state,
    club: { ...state.club, resources, researchCapacity: resources.research },
    leftField: { ...leftField, pendingDelayedEffects: leftField.pendingDelayedEffects.filter(effect => !dueIds.has(effect.id)) },
  }
}

export const prepareLeftFieldEvent = (rawState: GameState): GameState => {
  let state = { ...rawState, leftField: hydrateLeftFieldState(rawState.leftField) }
  const key = roundKey(state)
  if (state.leftField.currentEvent || state.leftField.processedRounds.includes(key)) return state
  const validPreMatchBoundary = !state.matchResult && !state.consequenceReport && WEEK_PLANNING_PHASES.has(state.phase)
  if (!validPreMatchBoundary) {
    return { ...state, leftField: { ...state.leftField, processedRounds: [...state.leftField.processedRounds, key] } }
  }
  state = applyDueLeftFieldEffects(state)
  const processedRounds = [...state.leftField.processedRounds, key]
  const forced = state.leftField.pendingDelayedEffects
    .filter(effect => effect.kind === 'follow-up' && dueNow(state, effect) && ARCHETYPES.includes(effect.followUpArchetype as LeftFieldArchetype))
    .sort((a, b) => String(a.id).localeCompare(String(b.id)))[0]
  const eligible = LEFT_FIELD_SCENARIOS
    .filter(scenario => isLeftFieldScenarioEligible(state, scenario) && (!forced || scenario.archetype === forced.followUpArchetype))
    .slice()
    .sort((a, b) => a.id.localeCompare(b.id))
  const shouldCreate = Boolean(forced) || draw(`${identity(state)}:event-v53-1538156`) < eventChance(state)
  if (!shouldCreate || !eligible.length) return { ...state, leftField: { ...state.leftField, processedRounds } }
  const recent = new Set(state.leftField.eventHistory.slice(-4).map(event => event.scenarioId))
  const scenario = weightedPick(eligible, candidate => {
    const severity = SEVERITIES.includes(candidate.severity as LeftFieldSeverity) ? candidate.severity! : 'moderate'
    return candidate.weight * SEVERITY_WEIGHT[severity] * (recent.has(candidate.id) ? 0.65 : 1)
  }, `${identity(state)}:scenario`)
  if (!scenario) return { ...state, leftField: { ...state.leftField, processedRounds } }
  const consumedFollowUpId = forced?.id
  return {
    ...state,
    leftField: {
      ...state.leftField,
      currentEvent: makeEvent(state, scenario),
      processedRounds,
      pendingDelayedEffects: consumedFollowUpId
        ? state.leftField.pendingDelayedEffects.filter(effect => effect.id !== consumedFollowUpId)
        : state.leftField.pendingDelayedEffects,
    },
  }
}

export const hasPendingLeftFieldEvent = (state: GameState) => state.leftField?.currentEvent?.status === 'pending'

const resourcePrecision = (value: number) => Math.round(value * 100) / 100

const applyResourceDelta = (state: GameState, resources: Resources, delta: Partial<Resources>): Resources => ({
  cash: resourcePrecision(Math.max(0, finite(resources.cash) + finite(delta.cash))),
  research: resourcePrecision(clamp(finite(resources.research) + finite(delta.research), 0, finite(state.club.maxResearchCapacity, 12))),
  development: resourcePrecision(clamp(finite(resources.development) + finite(delta.development), 0, Math.max(finite(state.club.developmentCapacity, 10), finite(resources.development)))),
  institutional: resourcePrecision(clamp(finite(resources.institutional) + finite(delta.institutional), 0, 100)),
})

const absoluteDue = (state: GameState, rounds: number) => {
  const rawFinalRound = state.season?.fixtures.length
    ? Math.max(...state.season.fixtures.map(fixture => fixture.round))
    : 10
  const finalRound = isFiniteNumber(rawFinalRound) && rawFinalRound > 0 ? Math.floor(rawFinalRound) : 10
  const season = isPositiveInteger(state.season?.season) ? state.season.season : 1
  const currentRound = isPositiveInteger(state.round) ? state.round : 1
  const delay = sanitiseDelayRounds(rounds) ?? 1
  const zeroBasedTotal = currentRound - 1 + delay
  const seasonOffset = Math.floor(zeroBasedTotal / finalRound)
  return {
    dueSeason: season + seasonOffset,
    dueRound: (zeroBasedTotal % finalRound) + 1,
  }
}

const scheduleOutcome = (state: GameState, event: LeftFieldEvent, outcome: LeftFieldOutcome, outcomeId: string) => {
  const effects = (outcome.delayedEffects ?? [])
    .map(sanitiseDelayedEffect)
    .filter((effect): effect is LeftFieldDelayedEffect => effect !== null)
    .map((effect, index): LeftFieldDelayedEffect => ({
    ...clone(effect),
    ...absoluteDue(state, effect.rounds),
    id: `${event.id}:${outcomeId}:effect-${index}`,
    sourceEventId: event.id,
    kind: 'resource',
    }))
  let followUp: LeftFieldDelayedEffect | undefined
  const sanitisedOutcome = sanitiseOutcome(outcome)
  const followUpRange = sanitisedOutcome?.followUp
  if (followUpRange && draw(`${event.id}:${outcomeId}:follow-up-roll`) < clamp(followUpRange.probability, 0, 1)) {
    const span = followUpRange.maxRounds - followUpRange.minRounds
    const delay = followUpRange.minRounds + Math.floor(draw(`${event.id}:${outcomeId}:follow-up-delay`) * (span + 1))
    followUp = {
      id: `${event.id}:${outcomeId}:follow-up`,
      sourceEventId: event.id,
      rounds: delay,
      ...absoluteDue(state, delay),
      description: `Follow-up to ${event.title ?? event.scenarioId}`,
      resourceDelta: {},
      kind: 'follow-up',
      followUpArchetype: followUpRange.archetype,
    }
  }
  return { effects: followUp ? [...effects, followUp] : effects, followUp }
}

const resolve = (state: GameState, choice: LeftFieldChoice, resolutionPath: 'coach' | 'delegated'): GameState => {
  const event = state.leftField.currentEvent
  if (!event || event.status !== 'pending' || !choiceEligible(state, choice) || !choiceAffordable(state, choice)) return state
  const outcomes = choice.outcomes ?? []
  const outcome = weightedPick(outcomes, candidate => candidate.probability, `${event.id}:${choice.id}:outcome`)
  const outcomeIndex = outcome ? outcomes.indexOf(outcome) : -1
  const outcomeId = outcome ? outcome.id ?? `outcome-${outcomeIndex}` : undefined
  let resources = applyResourceDelta(state, state.club.resources, choice.resourceDelta)
  if (outcome) resources = applyResourceDelta(state, resources, outcome.resourceDelta)
  const scheduled = outcome && outcomeId ? scheduleOutcome(state, event, outcome, outcomeId) : { effects: [], followUp: undefined }
  const resolved: LeftFieldEvent = {
    ...event,
    choices: event.choices?.some(item => item.id === choice.id) ? event.choices : [...(event.choices ?? []), clone(choice)],
    status: 'resolved',
    chosenChoiceId: choice.id,
    resolvedOutcomeId: outcomeId,
    resolvedOutcomes: outcome ? [clone(outcome)] : [],
    resolutionPath,
    pendingDelayedEffects: clone(scheduled.effects),
    followUpScheduled: scheduled.followUp ? {
      archetype: scheduled.followUp.followUpArchetype!,
      dueSeason: scheduled.followUp.dueSeason,
      dueRound: scheduled.followUp.dueRound!,
    } : undefined,
  }
  return {
    ...state,
    club: { ...state.club, resources, researchCapacity: resources.research },
    leftField: {
      ...state.leftField,
      currentEvent: null,
      eventHistory: state.leftField.eventHistory.some(item => item.id === resolved.id)
        ? state.leftField.eventHistory
        : [...state.leftField.eventHistory, resolved],
      pendingDelayedEffects: [...state.leftField.pendingDelayedEffects, ...scheduled.effects],
    },
  }
}

export const resolveLeftFieldChoice = (state: GameState, choiceId: string): GameState => {
  const event = state.leftField?.currentEvent
  if (!event || event.status !== 'pending') return state
  if (choiceId === LEFT_FIELD_CONTINGENCY_CHOICE_ID && availableLeftFieldChoices(state, event).length === 0) {
    return resolve(state, contingencyChoice(), 'coach')
  }
  const choice = (event.choices ?? scenarioById(event.scenarioId)?.choices ?? []).find(candidate => candidate.id === choiceId)
  return choice ? resolve(state, choice, 'coach') : state
}

export const delegateLeftFieldEvent = (state: GameState): GameState => {
  const event = state.leftField?.currentEvent
  if (!event || event.status !== 'pending') return state
  const choices = availableLeftFieldChoices(state, event)
  if (!choices.length) return resolve(state, contingencyChoice(), 'delegated')
  const posture = state.clubStrategy?.policies?.institutionalPosture ?? 'balanced'
  const ranked = choices.slice().sort((left, right) => {
    const score = (choice: LeftFieldChoice) => {
      const delta = choice.resourceDelta
      const stewardship = posture === 'protect' ? finite(delta.institutional) * 5 + finite(delta.cash) * 2 : posture === 'spend-to-win' ? finite(delta.development) * 3 + finite(delta.research) * 2 : 0
      const staffFit = choice.requires?.hasStaff && state.staffMarket.roster.some(member => member.specialty === choice.requires!.hasStaff!.specialty) ? 25 : 0
      return stewardship + staffFit + draw(`${event.id}:delegate:${choice.id}`) * 10
    }
    return score(right) - score(left) || left.id.localeCompare(right.id)
  })
  return resolve(state, ranked[0], 'delegated')
}
